/**
 * ChatInput — the ONE chat input (TASK-711).
 *
 * Auto-resize textarea, Enter sends / Shift+Enter newline (IME-safe), voice
 * dictation lifted from InputDock (useAudioRecorder + transcribeVoiceNote,
 * incl. the too-short-to-be-speech guard); transcript appends to the input —
 * dock convention, never auto-sends.
 */

import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mic, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { transcribeVoiceNote } from '../../services/voiceNoteService';

/** Blobs below this are click noise, not speech (same guard as InputDock). */
const MIN_SPEECH_BYTES = 1200;

export interface ChatInputHandle {
  focus: () => void;
  /** TASK-745: prefill the input (Regenerate proposes a message, never auto-sends). */
  setValue: (text: string) => void;
}

export interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Show the mic button (default true). */
  voice?: boolean;
  /** Extra controls rendered before the send button (e.g. attach). */
  trailing?: React.ReactNode;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  { onSend, disabled = false, placeholder, voice = true, trailing },
  ref
) {
  const { t } = useTranslation('common');
  const effectivePlaceholder = placeholder ?? t('dock.placeholder', 'Ask anything… (/ to focus)');
  const [value, setValue] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recorder = useAudioRecorder();

  useImperativeHandle(
    ref,
    () => ({
      focus: () => textareaRef.current?.focus(),
      setValue: (text: string) => {
        setValue(text);
        textareaRef.current?.focus();
      },
    }),
    [],
  );

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    setValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    onSend(text);
  };

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  async function toggleMic() {
    if (recorder.state === 'recording') {
      setTranscribing(true);
      try {
        const blob = await recorder.stop();
        if (blob.size < MIN_SPEECH_BYTES) return;
        const { transcript } = await transcribeVoiceNote(blob);
        if (transcript) setValue((v) => (v ? `${v} ${transcript}` : transcript));
      } catch {
        toast.error('Could not transcribe audio');
      } finally {
        setTranscribing(false);
      }
    } else {
      const ok = await recorder.start();
      if (!ok) toast.error('Microphone unavailable');
    }
  }

  const recording = recorder.state === 'recording';

  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={value}
        rows={1}
        disabled={disabled}
        onChange={(e) => {
          setValue(e.target.value);
          autoResize();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={effectivePlaceholder}
        className="max-h-[120px] min-h-9 flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
      />
      {voice && (
        <button
          type="button"
          onClick={toggleMic}
          disabled={disabled || transcribing}
          title={recording ? t('dock.stopRecording', 'Stop recording') : t('dock.dictate', 'Dictate')}
          aria-label={recording ? t('dock.stopRecording', 'Stop recording') : t('dock.dictate', 'Dictate')}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border transition-colors disabled:opacity-50 ${
            recording
              ? 'animate-pulse bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent'
          }`}
        >
          {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
        </button>
      )}
      {trailing}
      <button
        type="button"
        onClick={submit}
        disabled={disabled || !value.trim()}
        aria-label={t('dock.send', 'Send')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        <Send className="h-4 w-4" />
      </button>
    </div>
  );
});
